import React from "react";
import { motion } from "framer-motion";
import { Lock, Globe, Shield, ShieldCheck, ArrowRight, Home, Rocket, BarChart3, Phone, Facebook, Link2, Image, Sparkles } from "lucide-react";
import { fadeIn, fadeInUpSmall } from "../../lib/animations";

const badges = [
  { id: "badge-ssl", icon: Lock, label: "256-bit SSL Encrypted" },
  { id: "badge-gdpr", icon: Globe, label: "GDPR Compliant" },
  { id: "badge-fha", icon: ShieldCheck, label: "Fair Housing Compliant AI" },
  { id: "badge-soc", icon: Shield, label: "SOC 2 Ready" },
];

/* ─── Browser Frame Wrapper ─── */
function BrowserFrame({ url, badge, children }) {
  return (
    <div className="bg-white rounded-[10px] overflow-hidden shadow-lg border border-[#e5e5e5] relative">
      <div className="absolute top-3 left-3 z-20 w-7 h-7 rounded-full bg-[#39FF14] flex items-center justify-center text-xs font-bold text-black shadow">{badge}</div>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#f7f7f8] border-b border-[#e5e5e5]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        </div>
        <div className="flex-1 mx-4">
          <div className="bg-white rounded px-3 py-1 text-gray-400 text-[11px] max-w-[220px] mx-auto text-center border border-gray-200 truncate">{url}</div>
        </div>
      </div>
      <div className="bg-[#FAFAFA]">{children}</div>
      <div className="bg-gray-100 border-t border-gray-200 py-1.5 text-center" data-testid="mockup-sample-watermark">
        <p className="text-[9px] text-gray-400 italic tracking-wide">Sample preview — illustrative data only</p>
      </div>
    </div>
  );
}

/* ─── Mockup 1: Property Analysis + Nano Banana ─── */
function PropertyMockup() {
  return (
    <div className="p-4 space-y-4 text-left text-sm">
      {/* Real property image */}
      <div className="h-36 rounded-lg overflow-hidden relative">
        <img
          src="https://images.unsplash.com/photo-1597285112431-115a1f48bde2?w=600&h=300&fit=crop&q=80"
          alt="Luxury waterfront property"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm text-[10px] font-medium text-gray-600 px-2 py-0.5 rounded">single_family</div>
      </div>
      {/* Details */}
      <div className="flex justify-between items-start">
        <div>
          <p className="text-[10px] text-[#39FF14] font-semibold tracking-wider uppercase">Property Details</p>
          <p className="text-base font-bold text-gray-900 leading-tight">456 Ocean Drive</p>
          <p className="text-xs text-gray-500">Miami Beach, FL</p>
        </div>
        <p className="text-lg font-bold text-[#39FF14]">$1,754,000</p>
      </div>
      <div className="flex gap-4 text-xs text-gray-600">
        <span className="font-semibold">5 <span className="font-normal text-gray-400">Beds</span></span>
        <span className="font-semibold">2 <span className="font-normal text-gray-400">Baths</span></span>
        <span className="font-semibold">4,415 <span className="font-normal text-gray-400">Sqft</span></span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {["Gourmet Kitchen", "Waterfront", "Smart Home", "2-Car Garage"].map(t => (
          <span key={t} className="bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded-full border border-gray-200">{t}</span>
        ))}
      </div>

      {/* Nano Banana AI Creative */}
      <div className="border-t border-gray-200 pt-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#8B5CF6]/10 to-[#D946EF]/10 px-2 py-1 rounded-full border border-[#8B5CF6]/20">
            <Image className="w-3 h-3 text-[#8B5CF6]" />
            <span className="text-[9px] font-semibold text-[#8B5CF6]">NANO BANANA</span>
          </div>
          <span className="text-[9px] text-gray-400">AI Image Generation</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          <div className="h-16 rounded-md overflow-hidden relative bg-gradient-to-br from-blue-100 via-cyan-50 to-purple-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#8B5CF6]/40" />
            <div className="absolute bottom-0.5 right-0.5 bg-[#39FF14] rounded-full w-3.5 h-3.5 flex items-center justify-center">
              <span className="text-[7px] font-bold text-black">AI</span>
            </div>
          </div>
          <div className="h-16 rounded-md overflow-hidden relative bg-gradient-to-br from-amber-50 via-orange-50 to-rose-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#D946EF]/40" />
            <div className="absolute bottom-0.5 right-0.5 bg-[#39FF14] rounded-full w-3.5 h-3.5 flex items-center justify-center">
              <span className="text-[7px] font-bold text-black">AI</span>
            </div>
          </div>
          <div className="h-16 rounded-md overflow-hidden relative bg-gradient-to-br from-green-50 via-emerald-50 to-cyan-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#39FF14]/40" />
            <div className="absolute bottom-0.5 right-0.5 bg-[#39FF14] rounded-full w-3.5 h-3.5 flex items-center justify-center">
              <span className="text-[7px] font-bold text-black">AI</span>
            </div>
          </div>
        </div>
        <p className="text-[10px] text-[#39FF14] font-semibold tracking-wider uppercase mb-1">Your AI Creative Department</p>
        <p className="text-sm font-bold text-gray-900 mb-2">WINNING COPY, READY TO LAUNCH</p>
        <div className="space-y-2">
          <div className="bg-white rounded-lg p-2.5 border border-gray-200">
            <p className="text-[10px] font-semibold text-gray-800 mb-0.5">The Urgency Hook</p>
            <p className="text-[11px] text-gray-500 leading-snug">Luxury waterfront living won't wait. 456 Ocean Drive — 5BD/2BA on Miami Beach. Schedule your private tour before it's gone.</p>
          </div>
          <div className="bg-white rounded-lg p-2.5 border border-[#39FF14]/40">
            <div className="flex justify-between items-center mb-0.5">
              <p className="text-[10px] font-semibold text-gray-800">The Benefit-Driven</p>
              <span className="text-[9px] bg-[#39FF14]/20 text-[#39FF14] px-1.5 py-0.5 rounded font-medium">Selected</span>
            </div>
            <p className="text-[11px] text-gray-500 leading-snug">Wake up to ocean views every morning. This 4,415 sqft smart home features a gourmet kitchen, private dock, and steps to South Beach.</p>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 text-[10px]">
        {["ALL STRATEGIES", "LUXURY SEEKER", "FAMILY BUYER", "INVESTOR"].map((t, i) => (
          <span key={t} className={`px-2 py-1 rounded font-medium ${i === 0 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>{t}</span>
        ))}
      </div>
      <button className="w-full bg-[#39FF14] text-black text-xs font-bold py-2.5 rounded-lg flex items-center justify-center gap-1.5">
        <Rocket className="w-3.5 h-3.5" /> Launch Campaign
      </button>
    </div>
  );
}

/* ─── Mockup 2: Dashboard ─── */
function DashboardMockup() {
  const funnel = [
    { label: "Ads Shown", value: "8,200" },
    { label: "Clicks", value: "640" },
    { label: "Leads", value: "47" },
    { label: "Showings", value: "12" },
  ];
  return (
    <div className="p-4 space-y-4 text-left text-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-[#39FF14]/20 flex items-center justify-center"><Home className="w-3.5 h-3.5 text-[#39FF14]" /></div>
          <span className="text-xs font-semibold text-gray-700">Dashboard</span>
        </div>
        <button className="bg-[#39FF14] text-black text-[10px] font-bold px-3 py-1.5 rounded-lg flex items-center gap-1">
          <Rocket className="w-3 h-3" /> DEPLOY NEW LISTING
        </button>
      </div>
      <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
        <p className="text-[10px] text-gray-400 tracking-wider uppercase mb-2">Leads Generated This Month</p>
        <p className="text-5xl font-extrabold text-[#39FF14] leading-none">47</p>
      </div>
      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <p className="text-[10px] text-gray-400 tracking-wider uppercase mb-3">Marketing Funnel</p>
        <div className="flex items-center justify-between">
          {funnel.map((s, i) => (
            <div key={s.label} className="flex items-center">
              <div className="text-center">
                <p className={`text-lg font-bold ${i === funnel.length - 1 ? 'text-[#39FF14]' : 'text-gray-900'}`}>{s.value}</p>
                <p className="text-[10px] text-gray-400">{s.label}</p>
              </div>
              {i < funnel.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-gray-300 mx-2 flex-shrink-0" />}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[
          { l: "Campaigns", v: "12", c: "#39FF14" },
          { l: "Ad Spend", v: "$1.2k", c: "#22C55E" },
          { l: "Properties", v: "60", c: "#8B5CF6" },
          { l: "Voice AI", v: "24/7", c: "#D946EF" },
        ].map(s => (
          <div key={s.l} className="bg-white rounded-lg p-2 border border-gray-200 text-center">
            <p className="text-base font-bold" style={{ color: s.c }}>{s.v}</p>
            <p className="text-[9px] text-gray-400">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="bg-gray-900 rounded-lg py-2 px-3 flex items-center gap-3 overflow-hidden">
        {[
          { action: "Lead qualified", city: "Nashville" },
          { action: "AI calling now", city: "Miami" },
          { action: "Showing booked", city: "Austin" },
        ].map((t) => (
          <div key={`${t.action}-${t.city}`} className="flex items-center gap-1.5 text-[10px] whitespace-nowrap flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
            <span className="text-gray-400">{t.action}</span>
            <span className="text-white font-medium">in {t.city}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Mockup 3: Integrations (no Zapier, added Vapi Voice AI) ─── */
function IntegrationsMockup() {
  const integrations = [
    { name: "Meta Marketing", desc: "Facebook & Instagram Ads", icon: Facebook, iconBg: "#1877F2", status: "Not Connected", statusColor: "#F97316" },
    { name: "HubSpot CRM", desc: "Auto-push leads to your CRM", icon: Link2, iconBg: "#39FF14", status: "Active", statusColor: "#22C55E" },
    { name: "Vapi Voice AI", desc: "24/7 AI lead follow-up calls", icon: Phone, iconBg: "#8B5CF6", status: "Active", statusColor: "#22C55E" },
    { name: "MLS Status Guard", desc: "Auto-pause ads on listing changes", icon: Home, iconBg: "#39FF14", status: "Not Connected", statusColor: "#F97316" },
  ];
  return (
    <div className="p-4 space-y-4 text-left text-sm">
      <div className="flex items-center justify-between">
        <p className="text-base font-bold text-gray-900">Integrations</p>
        <button className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-gray-600"><BarChart3 className="w-3 h-3" /> Refresh</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {["System Status", "3 Active", "1 Linked", "MLS Guard"].map((p, i) => (
          <span key={p} className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${i === 0 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>{i === 0 && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#39FF14] mr-1 align-middle" />}{p}</span>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {integrations.map(int => (
          <div key={int.name} className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-start justify-between mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${int.iconBg}15` }}>
                <int.icon className="w-4 h-4" style={{ color: int.iconBg }} />
              </div>
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${int.statusColor}15`, color: int.statusColor }}>{int.status}</span>
            </div>
            <p className="text-xs font-semibold text-gray-900">{int.name}</p>
            <p className="text-[10px] text-gray-400 leading-snug mt-0.5">{int.desc}</p>
          </div>
        ))}
      </div>
      <div className="bg-gray-100 rounded-lg py-3 text-center border border-gray-200">
        <p className="text-[10px] text-gray-400">More Integrations Coming Soon</p>
        <p className="text-[9px] text-gray-300">kvCORE, Sierra Interactive, Follow Up Boss, and more</p>
      </div>
    </div>
  );
}

/* ─── Main Section ─── */
export function TrustSection() {
  return (
    <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-white" data-testid="trust-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          className="mb-8 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Built for Real Estate Professionals
          </h2>
          <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto mb-2">
            A clean, intuitive dashboard designed to give you full control.
          </p>
          <p className="text-xs text-gray-400 italic">Sneak peek of the platform</p>
        </motion.div>

        {/* Flow Indicator */}
        <div className="flex items-center justify-center gap-0 mb-12 flex-wrap">
          {[
            { num: "1", label: "Analyze Listing" },
            { num: "2", label: "Launch & Track" },
            { num: "3", label: "Connect Everything" },
          ].map((step, i) => (
            <div key={step.num} className="flex items-center">
              <div className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-full text-xs font-medium">
                <span className="w-5 h-5 rounded-full bg-[#39FF14] text-black flex items-center justify-center text-[10px] font-bold">{step.num}</span>
                {step.label}
              </div>
              {i < 2 && (
                <div className="w-8 h-px bg-gray-300 mx-1 hidden sm:block" />
              )}
            </div>
          ))}
        </div>

        {/* 3-Column Mockup Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
          <motion.div initial={{ opacity: 0.99, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.05 }} transition={{ duration: 0.5 }}>
            <BrowserFrame url="adwup.ai/property/456-ocean-drive" badge="1">
              <PropertyMockup />
            </BrowserFrame>
            <div className="mt-3 text-center">
              <p className="text-sm font-bold text-gray-900">AI Property Analysis</p>
              <p className="text-xs text-gray-500">Paste any listing URL — AI builds the ad in seconds.</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0.99, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.05 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <BrowserFrame url="adwup.ai/dashboard" badge="2">
              <DashboardMockup />
            </BrowserFrame>
            <div className="mt-3 text-center">
              <p className="text-sm font-bold text-gray-900">Marketing Dashboard</p>
              <p className="text-xs text-gray-500">Track every lead from first click to booked showing.</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0.99, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.05 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <BrowserFrame url="adwup.ai/integrations" badge="3">
              <IntegrationsMockup />
            </BrowserFrame>
            <div className="mt-3 text-center">
              <p className="text-sm font-bold text-gray-900">Live Integrations</p>
              <p className="text-xs text-gray-500">Connect Meta, HubSpot, MLS, and more with one click.</p>
            </div>
          </motion.div>
        </div>

        {/* Security Badges */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.05 }}
          className="flex flex-wrap items-center justify-center gap-8"
        >
          {badges.map((badge) => (
            <div key={badge.id} className="flex items-center gap-2 text-gray-400 text-sm">
              <badge.icon className="w-4 h-4" />
              <span>{badge.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
